import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Footer } from '../components';
import { allArticles } from '../data/articles';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';
import { useGSAPReveal } from '../util/useGSAPReveal';

const articlesListSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Technical Articles by Asim Hafeez',
  description:
    'Technical articles and insights on AI engineering, blockchain, system design, machine learning, and cloud architecture by Asim Hafeez.',
  numberOfItems: allArticles.length,
  itemListElement: allArticles.map((article, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    url: article.link,
    name: article.title,
  })),
};

const articlesBreadcrumbs = generateBreadcrumbSchema([
  { name: 'Home', url: 'https://asimhafeez.com/' },
  { name: 'Articles', url: 'https://asimhafeez.com/articles' },
]);

const filterCategories = ['All', 'AI Engineering', 'Blockchain', 'AWS', 'System Design', 'Machine Learning'];
const INITIAL_DISPLAY_COUNT = 6;
const DISPLAY_INCREMENT = 6;

const parseArticleDate = (date?: string) => {
  if (!date) {
    return null;
  }

  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatArticleDate = (date?: string) => {
  const parsed = parseArticleDate(date);

  return parsed
    ? parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Undated';
};

const ArticlesPage: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<string>('All');
  const [isMobileView, setIsMobileView] = useState(false);
  const [displayCount, setDisplayCount] = useState(INITIAL_DISPLAY_COUNT);
  const archiveRef = useRef<HTMLElement | null>(null);

  const headerRef = useGSAPReveal({ variant: 'fade-up', start: 'top 95%' });
  const spotlightRef = useGSAPReveal({ variant: 'fade-up', delay: 0.08, start: 'top 95%' });
  const filtersRef = useGSAPReveal({ variant: 'fade-up', delay: 0.12, start: 'top 95%' });
  const listRef = useGSAPReveal({ variant: 'stagger-children', delay: 0.15, staggerAmount: 0.05, start: 'top 95%' });

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const sortedArticles = useMemo(() => {
    return [...allArticles].sort((a, b) => {
      const left = parseArticleDate(a.date)?.getTime() || 0;
      const right = parseArticleDate(b.date)?.getTime() || 0;
      return right - left;
    });
  }, []);

  const filteredArticles = useMemo(() => {
    if (activeFilter === 'All') {
      return sortedArticles;
    }

    return sortedArticles.filter((article) => article.tags?.includes(activeFilter));
  }, [activeFilter, sortedArticles]);

  const featuredArticle = filteredArticles[0] || sortedArticles[0];
  const archiveArticles = (activeFilter === 'All' ? sortedArticles : filteredArticles)
    .slice(0, displayCount);
  const hasMoreArticles = displayCount < filteredArticles.length;
  const canShowLess = displayCount > INITIAL_DISPLAY_COUNT;

  useEffect(() => {
    setDisplayCount(INITIAL_DISPLAY_COUNT);
  }, [activeFilter]);

  const handleShowLess = () => {
    setDisplayCount(INITIAL_DISPLAY_COUNT);

    requestAnimationFrame(() => {
      archiveRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <div>
      <SEO
        title="Articles & Insights — Asim Hafeez"
        description="Technical articles and insights by Asim Hafeez covering AI engineering, blockchain development, system design, machine learning, AWS cloud architecture, and modern software engineering best practices. 30+ in-depth technical articles."
        canonicalUrl="/articles"
        keywords="asim hafeez articles, AI engineering blog, blockchain development, system design articles, machine learning tutorials, AWS cloud architecture, software engineering insights, technical blog, medium articles"
        jsonLd={[articlesListSchema, articlesBreadcrumbs]}
        articleAuthor="Asim Hafeez"
      />

      <section
        className="articles-page"
        style={{
          minHeight: '100vh',
          background: 'transparent',
          padding: isMobileView ? '92px 20px 96px' : '132px 48px 120px',
          position: 'relative',
          overflow: 'visible',
        }}
      >
        <div className="articles-shell">
          <header ref={headerRef} className="articles-hero">
            <div className="articles-kicker">Writing archive</div>
            <div className="articles-hero__row">
              <div className="articles-hero__copy">
                <h1>My Writing Archive</h1>
                <p>
                  I write about AI engineering, backend systems, blockchain, and software
                  architecture through lessons, ideas, and patterns that come out of my real work.
                </p>
              </div>

              <div className="articles-hero__meta">
                <div className="articles-hero__stat">
                  <span>{allArticles.length}+</span>
                  <p>published articles</p>
                </div>
                <div className="articles-hero__note">
                  A personal collection of articles shaped by what I build, study, and learn along the way.
                </div>
              </div>
            </div>
          </header>

          {featuredArticle ? (
            <section ref={spotlightRef} className="articles-spotlight">
              <div className="articles-spotlight__image">
                <img src={featuredArticle.thumbnail} alt={featuredArticle.title} loading="lazy" />
              </div>

              <div className="articles-spotlight__content">
                <div className="articles-spotlight__topline">
                  <span>{featuredArticle.platform || 'Article'}</span>
                  <span>{formatArticleDate(featuredArticle.date)}</span>
                </div>

                <h2>{featuredArticle.title}</h2>

                <p>
                  {featuredArticle.publishedIn || 'A featured entry from the archive, surfaced to lead the page without overloading it.'}
                </p>

                {featuredArticle.tags?.length ? (
                  <div className="articles-tag-strip">
                    {featuredArticle.tags.slice(0, 3).map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                ) : null}

                <a
                  href={featuredArticle.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="articles-spotlight__link"
                >
                  Read article
                </a>
              </div>
            </section>
          ) : null}

          <section ref={archiveRef} className="articles-archive">
            <div className="articles-archive__header">
              <div>
                <div className="articles-kicker">Browse by theme</div>
                <h2>Browse what I’ve been writing about.</h2>
              </div>
              <p>
                This archive is organized to stay simple and personal, so the writing remains the focus.
              </p>
            </div>

            <div ref={filtersRef} className="articles-filters" role="tablist" aria-label="Article categories">
              {filterCategories.map((category) => {
                const isActive = activeFilter === category;

                return (
                  <button
                    key={category}
                    type="button"
                    className={`articles-filter-chip${isActive ? ' is-active' : ''}`}
                    onClick={() => setActiveFilter(category)}
                  >
                    {category}
                  </button>
                );
              })}
            </div>

            {filteredArticles.length === 0 ? (
              <div className="articles-empty-state">
                <div>No articles found for "{activeFilter}".</div>
                <p>Choose another filter to reopen the archive.</p>
              </div>
            ) : (
              <div ref={listRef} className="articles-list">
                {archiveArticles.map((article, index) => (
                  <a
                    key={`${article.title}-${article.date}-${index}`}
                    href={article.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="article-row"
                  >
                    <div className="article-row__body">
                      <h3 className="article-title">{article.title}</h3>

                      <div className="article-row__tags">
                        {(article.tags?.length ? article.tags : ['Engineering']).slice(0, 3).map((tag, tagIndex, tags) => (
                          <span key={tag}>
                            {tag}
                            {tagIndex < tags.length - 1 ? <span className="article-row__tag-separator">•</span> : null}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="article-row__side">
                      <span>{article.platform || 'Article'}</span>
                      <div className="article-row__date">{formatArticleDate(article.date)}</div>
                      <div className="article-row__arrow">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="7" y1="17" x2="17" y2="7"></line>
                          <polyline points="7 7 17 7 17 17"></polyline>
                        </svg>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}

            {filteredArticles.length > INITIAL_DISPLAY_COUNT && (
              <div className="articles-actions">
                {hasMoreArticles ? (
                  <button
                    type="button"
                    className="articles-action-button articles-action-button--more"
                    onClick={() => setDisplayCount((prev) => Math.min(prev + DISPLAY_INCREMENT, filteredArticles.length))}
                  >
                    <span>Show more</span>
                    <span className="articles-action-button__icon" aria-hidden="true">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </span>
                  </button>
                ) : null}

                {canShowLess ? (
                  <button
                    type="button"
                    className="articles-action-button articles-action-button--less"
                    onClick={handleShowLess}
                  >
                    <span>Show less</span>
                    <span className="articles-action-button__icon" aria-hidden="true">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="18 15 12 9 6 15"></polyline>
                      </svg>
                    </span>
                  </button>
                ) : null}
              </div>
            )}
          </section>
        </div>
      </section>

      <style>{`
        .articles-shell {
          max-width: 1180px;
          margin: 0 auto;
        }

        .articles-kicker {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 18px;
          color: rgba(245, 245, 245, 0.5);
          font-family: var(--font-mono);
          font-size: 11px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
        }

        .articles-kicker::before {
          content: '';
          width: 36px;
          height: 1px;
          background: linear-gradient(90deg, rgba(196, 149, 106, 0.95), rgba(196, 149, 106, 0));
        }

        .articles-hero {
          margin-bottom: 56px;
        }

        .articles-hero__row {
          display: grid;
          grid-template-columns: minmax(0, 1.4fr) minmax(240px, 0.6fr);
          gap: 32px;
          align-items: end;
          padding-bottom: 28px;
          border-bottom: 1px solid rgba(245, 245, 245, 0.08);
        }

        .articles-hero__copy h1 {
          max-width: 760px;
          font-size: clamp(40px, 6vw, 72px);
          line-height: 0.98;
          letter-spacing: -0.05em;
          margin-bottom: 16px;
        }

        .articles-hero__copy p {
          max-width: 620px;
          color: rgba(245, 245, 245, 0.68);
          font-size: clamp(16px, 2vw, 19px);
          line-height: 1.72;
        }

        .articles-hero__meta {
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          gap: 26px;
        }

        .articles-hero__stat,
        .articles-hero__note {
          padding: 18px 0 0;
          border-top: 1px solid rgba(245, 245, 245, 0.08);
        }

        .articles-hero__stat span {
          display: block;
          margin-bottom: 6px;
          color: var(--brand-primary);
          font-family: var(--font-heading);
          font-size: clamp(40px, 4.2vw, 64px);
          letter-spacing: -0.04em;
          line-height: 0.95;
        }

        .articles-hero__stat p {
          color: rgba(245, 245, 245, 0.46);
          font-family: var(--font-mono);
          font-size: 11px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        .articles-hero__note {
          color: rgba(245, 245, 245, 0.62);
          font-size: 15px;
          line-height: 1.75;
          max-width: 320px;
        }

        .articles-spotlight {
          display: grid;
          grid-template-columns: minmax(280px, 0.8fr) minmax(0, 1fr);
          gap: 28px;
          align-items: center;
          padding: 24px 0 36px;
          margin-bottom: 64px;
          border-bottom: 1px solid rgba(245, 245, 245, 0.08);
        }

        .articles-spotlight__image {
          height: 320px;
          overflow: hidden;
          border-radius: 24px;
          background: rgba(245, 245, 245, 0.03);
          border: 1px solid rgba(245, 245, 245, 0.08);
        }

        .articles-spotlight__image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .articles-spotlight__topline,
        .article-row__side span,
        .article-row__date {
          font-family: var(--font-mono);
          font-size: 11px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        .articles-spotlight__topline {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 14px;
          color: rgba(245, 245, 245, 0.42);
        }

        .articles-spotlight__topline span:first-child {
          color: var(--brand-primary);
        }

        .articles-spotlight__content h2 {
          font-size: clamp(30px, 4vw, 48px);
          line-height: 1.06;
          letter-spacing: -0.04em;
          margin-bottom: 14px;
        }

        .articles-spotlight__content p {
          max-width: 600px;
          color: rgba(245, 245, 245, 0.66);
          font-size: 16px;
          line-height: 1.74;
          margin-bottom: 18px;
        }

        .articles-tag-strip,
        .article-row__tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .articles-tag-strip span,
        .article-row__tags span,
        .articles-filter-chip {
          font-size: 10px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        .articles-tag-strip span,
        .article-row__tags span {
          display: inline-flex;
          align-items: center;
          min-height: 28px;
          padding: 0 10px;
          border-radius: 999px;
          border: 1px solid rgba(196, 149, 106, 0.14);
          background: linear-gradient(180deg, rgba(196, 149, 106, 0.06), rgba(245, 245, 245, 0.02));
          color: rgba(245, 245, 245, 0.72);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
        }

        .article-row__tags span {
          font-size: 11px;
          letter-spacing: 0.04em;
          text-transform: none;
        }

        .articles-tag-strip span {
          font-size: 10px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        .articles-spotlight__link,
        .articles-show-more {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 48px;
          padding: 0 22px;
          margin-top: 20px;
          border-radius: 999px;
          border: 1px solid rgba(196, 149, 106, 0.26);
          background: rgba(196, 149, 106, 0.08);
          color: var(--text-primary);
          font-family: var(--font-mono);
          font-size: 11px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          transition:
            transform var(--duration-normal) var(--ease-out),
            border-color var(--duration-normal) var(--ease-out),
            background var(--duration-normal) var(--ease-out),
            color var(--duration-normal) var(--ease-out);
        }

        .articles-spotlight__link {
          min-height: auto;
          padding: 0;
          margin-top: 18px;
          border: none;
          border-bottom: 1px solid rgba(196, 149, 106, 0.24);
          border-radius: 0;
          background: transparent;
          color: rgba(245, 245, 245, 0.78);
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 500;
          letter-spacing: 0.01em;
          text-transform: none;
          justify-content: flex-start;
        }

        .articles-spotlight__link:hover {
          transform: none;
          border-bottom-color: rgba(196, 149, 106, 0.56);
          background: transparent;
          color: var(--brand-primary);
        }

        .articles-archive__header {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(260px, 0.7fr);
          gap: 24px;
          align-items: end;
          margin-bottom: 30px;
        }

        .articles-archive__header h2 {
          font-size: clamp(28px, 4vw, 42px);
          line-height: 1.06;
          letter-spacing: -0.04em;
        }

        .articles-archive__header p {
          max-width: 400px;
          color: rgba(245, 245, 245, 0.58);
          font-size: 15px;
          line-height: 1.72;
        }

        .articles-filters {
          display: flex;
          gap: 0;
          overflow-x: auto;
          overflow-y: visible;
          margin-bottom: 30px;
          padding-bottom: 2px;
          scrollbar-width: none;
          -ms-overflow-style: none;
          border-bottom: 1px solid rgba(245, 245, 245, 0.06);
        }

        .articles-filters::-webkit-scrollbar {
          display: none;
        }

        .articles-filter-chip {
          min-width: fit-content;
          min-height: 44px;
          padding: 12px 20px;
          border: none;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
          background: transparent;
          color: rgba(245, 245, 245, 0.54);
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 400;
          letter-spacing: 0.01em;
          text-transform: none;
          cursor: pointer;
          transition:
            border-color var(--duration-normal) var(--ease-out),
            color var(--duration-normal) var(--ease-out);
        }

        .articles-filter-chip:hover {
          color: rgba(245, 245, 245, 0.78);
        }

        .articles-filter-chip.is-active {
          border-bottom-color: #C4956A;
          color: #F5F5F5;
        }

        .articles-list {
          display: flex;
          flex-direction: column;
          border-top: 1px solid rgba(245, 245, 245, 0.06);
        }

        .article-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 32px;
          padding: 40px 24px;
          border-bottom: 1px solid rgba(245, 245, 245, 0.06);
          text-decoration: none;
          position: relative;
          border-radius: 8px;
          margin: 0 -24px;
          transition:
            border-color var(--duration-normal) var(--ease-out),
            background-color var(--duration-normal) var(--ease-out),
            transform var(--duration-normal) var(--ease-out);
        }

        .article-row__body {
          flex: 1;
          max-width: 75%;
        }

        .article-row__body h3 {
          color: #E5E5E5;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 26px;
          font-weight: 500;
          line-height: 1.3;
          letter-spacing: -0.01em;
          margin-bottom: 12px;
          transition:
            color var(--duration-normal) var(--ease-out),
            letter-spacing var(--duration-normal) var(--ease-out);
        }

        .article-row__date {
          color: #525252;
          font-family: 'JetBrains Mono', monospace;
          font-size: 12px;
          letter-spacing: 0.05em;
          transition: color var(--duration-normal) var(--ease-out);
        }

        .article-row:hover {
          background: rgba(255, 255, 255, 0.02);
        }

        .article-row:hover .article-title {
          color: var(--brand-primary);
        }

        .article-row__side {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 4px;
          min-width: 180px;
        }

        .article-row__side span {
          color: #A3A3A3;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 500;
          letter-spacing: normal;
          text-transform: none;
          transition: color var(--duration-normal) var(--ease-out);
        }

        .article-row__arrow {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 1px solid rgba(196, 149, 106, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #C4956A;
          background: transparent;
          transition:
            transform var(--duration-normal) var(--ease-out),
            border-color var(--duration-normal) var(--ease-out),
            color var(--duration-normal) var(--ease-out),
            background var(--duration-normal) var(--ease-out);
        }

        .article-row:hover .article-row__arrow {
          transform: translate(3px, -3px);
          border-color: #C4956A;
          color: #111111;
          background: #C4956A;
        }

        .article-row__tags {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .article-row__tags span {
          padding: 0;
          min-height: auto;
          border: none;
          border-radius: 0;
          background: transparent;
          box-shadow: none;
          color: #737373;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }

        .article-row__tag-separator {
          color: #404040;
          margin: 0 8px;
        }

        .articles-empty-state {
          padding: 42px 0;
          border-bottom: 1px solid rgba(245, 245, 245, 0.08);
          text-align: center;
        }

        .articles-empty-state div {
          color: var(--text-primary);
          font-family: var(--font-heading);
          font-size: 24px;
          margin-bottom: 8px;
        }

        .articles-empty-state p {
          color: rgba(245, 245, 245, 0.52);
          font-size: 14px;
        }

        .articles-actions {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
          gap: 16px;
          width: 100%;
          margin-top: 40px;
        }

        .articles-action-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-height: auto;
          padding: 0 0 4px;
          position: relative;
          border: none;
          border-radius: 0;
          background: transparent;
          color: rgba(245, 245, 245, 0.84);
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 500;
          letter-spacing: 0.005em;
          line-height: 1;
          cursor: pointer;
          transition:
            transform var(--duration-normal) var(--ease-out),
            color var(--duration-normal) var(--ease-out);
        }

        .articles-action-button::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: -2px;
          height: 1px;
          background: rgba(196, 149, 106, 0.75);
          transform: scaleX(0.35);
          transform-origin: center;
          opacity: 0;
          transition:
            transform 220ms var(--ease-out),
            opacity 220ms var(--ease-out);
        }

        .articles-action-button:hover {
          transform: translateY(-1px);
          color: var(--brand-primary);
        }

        .articles-action-button:hover::after,
        .articles-action-button:focus-visible::after {
          transform: scaleX(1);
          opacity: 1;
        }

        .articles-action-button--more {
          color: rgba(245, 245, 245, 0.9);
        }

        .articles-action-button--less {
          color: rgba(245, 245, 245, 0.72);
        }

        .articles-action-button__icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(245, 245, 245, 0.1);
          flex-shrink: 0;
          transition:
            transform var(--duration-normal) var(--ease-out),
            border-color var(--duration-normal) var(--ease-out),
            background var(--duration-normal) var(--ease-out),
            color var(--duration-normal) var(--ease-out);
        }

        .articles-action-button:hover .articles-action-button__icon {
          border-color: rgba(196, 149, 106, 0.28);
          background: rgba(196, 149, 106, 0.08);
          color: var(--brand-primary);
        }

        .articles-action-button:focus-visible {
          outline: none;
          color: var(--brand-primary);
        }

        .articles-action-button:first-child:hover .articles-action-button__icon {
          transform: translateY(2px);
        }

        .articles-action-button:last-child:hover .articles-action-button__icon {
          transform: translateY(-2px);
        }

        @media (max-width: 900px) {
          .articles-hero__row,
          .articles-spotlight,
          .articles-archive__header {
            grid-template-columns: 1fr;
          }

          .article-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
            padding: 32px 0;
            margin: 0;
            border-radius: 0;
          }

          .article-row__body {
            max-width: 100%;
          }

          .article-row__side {
            align-items: flex-start;
            gap: 24px;
            min-width: 100%;
            flex-direction: row;
            justify-content: space-between;
            margin-top: 8px;
          }
        }

        @media (max-width: 768px) {
          .articles-hero {
            margin-bottom: 40px;
          }

          .articles-hero__copy h1 {
            font-size: 42px;
          }

          .articles-spotlight__image {
            height: 220px;
          }

          .articles-spotlight {
            padding: 20px 0 28px;
            margin-bottom: 48px;
          }

          .article-row__body h3 {
            font-size: 22px;
          }

          .articles-actions {
            gap: 10px;
            align-items: flex-start;
          }

          .articles-action-button {
            width: auto;
            justify-content: flex-start;
          }
        }
      `}</style>

      <Footer />
    </div>
  );
};

export default ArticlesPage;
